import { type ReactNode, Children, cloneElement } from 'react';
import Link from 'next/link';

import { LinkField, LinkType } from '@tmh-website/shared/cms';
import { LocalePrefix } from '@tmh-website/shared/const';
import { useParseLinkField } from '@tmh-website/shared/hooks';

interface CMSLinkProps {
  link: LinkField;
  children?: ReactNode;
  className?: string;
  /**
   * Allows to manually provide locale (when component is used in non NextJS environment)
   */
  locale?: LocalePrefix;
  /**
   * Replace Next Link with <a> tag
   */
  isStandaloneMode?: boolean;
}

const CMSLink = ({ link, children, className = '', locale, isStandaloneMode, ...props }: CMSLinkProps) => {
  const parseLinkField = useParseLinkField();

  const parsedLink = parseLinkField({ link, isStandaloneMode });

  if (parsedLink === null) return null;

  let child: any;

  try {
    child = Children.only(children);
  } catch (e) {
    // No child given, we need to render an <a> tag ourselves
  }

  const linkComponent = child ? (
    cloneElement(child, parsedLink.attributes)
  ) : (
    <a {...parsedLink.attributes} className={className} {...props}>
      {link.label}
    </a>
  );

  if (link.type === LinkType.URL || isStandaloneMode) {
    return cloneElement(linkComponent, { href: parsedLink.href });
  } else {
    return (
      <Link href={parsedLink.href} locale={locale} passHref legacyBehavior>
        {linkComponent}
      </Link>
    );
  }
};

export default CMSLink;
